"use client";

import React from "react";
import { cn } from "utils/utils";
import { SelectionCheckbox } from "modules/common/components/bulk-actions/SelectionCheckbox";
import { Checkbox } from "modules/common/components/ui/Checkbox";
import { useCardGridItemContext } from "./CardGridItemContext";

type ColorVariant = "primary" | "secondary" | "warning";

interface CardGridItemSelectionOverlayProps {
  /** Color variant for the checkbox — only used in "controlled" mode. Default: "primary" */
  variant?: ColorVariant;
  /** Keeps the control mounted but visually absent until selection starts. */
  visible?: boolean;
  /** Render visible but inert, e.g. out-of-stock inventory. */
  disabled?: boolean;
  /** Replaces the default aria-label when disabled, to explain why. */
  disabledLabel?: string;
  /**
   * Selection state for the inert branch. Availability refreshes in the
   * background, so a card ticked while it had free copies can go inert while
   * it sits in the selection — it is still counted and still submitted, and
   * an unchecked box would say otherwise. Only consulted when inert; the live
   * branches read the store / the controlled mode.
   */
  checked?: boolean;
}

export function CardGridItemSelectionOverlay({
  variant = "primary",
  visible = true,
  disabled = false,
  disabledLabel,
  checked = false,
}: CardGridItemSelectionOverlayProps) {
  const { item, selection } = useCardGridItemContext();

  // Cells that opt out arrive with `modeType: "none"`, so this can't come from
  // `selection` alone.
  const inert = disabled || selection.modeType === "none";

  // An unselectable card draws nothing — unless it is already IN the selection
  // (availability refreshes in the background), because the bulk action still
  // submits it and the user has to be able to untick it. Unmounting is safe
  // here only because inertness doesn't flip when a selection starts.
  if (inert && !checked) return null;

  const idle = !selection.isAnySelected;

  return (
    <div
      className={cn(
        "absolute left-1 top-1 z-10 transition-opacity duration-200",
        // Gate the HIDDEN state on hover capability, not the reveal: touch
        // reports `hover: none`, where a hover-gated control is unreachable
        // and the first tap lands on the card underneath.
        idle &&
          "can-hover:pointer-events-none can-hover:opacity-0 can-hover:group-hover:pointer-events-auto can-hover:group-hover:opacity-100 can-hover:focus-within:pointer-events-auto can-hover:focus-within:opacity-100",
        // Composes with the above rather than duplicating it: `invisible` wins,
        // so a caller withholding the control stays hidden through hover.
        !visible && "pointer-events-none invisible"
      )}
      aria-hidden={!visible || undefined}
    >
      {/* The pill goes opaque when inert: an unavailable cell is already dimmed
          as a whole (opacity + grayscale), and a translucent pill on top of
          that washes the control out until it reads as missing rather than
          disabled. */}
      <div
        className={cn(
          "flex items-center rounded-full",
          inert ? "bg-base-100" : "bg-base-100/80"
        )}
      >
        {inert && (
          <Checkbox
            checked={checked}
            onChange={() => {}}
            onClick={(e) => e.stopPropagation()}
            disabled
            variant={variant}
            // opacity-100 overrides Checkbox's own disabled opacity-50, which
            // would otherwise compound with the dimmed cell around it.
            className="rounded-full p-1 opacity-100"
            aria-label={disabledLabel ?? "Unavailable for selection"}
          />
        )}
        {!inert && selection.modeType === "store" && (
          <SelectionCheckbox ids={item.ids} />
        )}
        {!inert && selection.modeType === "controlled" && (
          <Checkbox
            checked={selection.state === "all"}
            onChange={selection.onToggle}
            onClick={(e) => e.stopPropagation()}
            variant={variant}
            className="rounded-full p-1"
            aria-label={`Select ${item.name}`}
          />
        )}
      </div>
    </div>
  );
}
